/**
 * RoutingMapper - Visual interface for tag-to-organization routing
 *
 * For OrgAdmin users only. Allows configuration of automatic report routing
 * based on tags (KPIs/Locations) to child organizations.
 * On mobile, this shows a read-only list of current routing rules.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { isWeb as checkIsWeb } from '../../util/platform';
import { View, StyleSheet, ActivityIndicator, Pressable } from 'react-native'
import { Feather } from '@expo/vector-icons';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';
import DropDownPicker from 'react-native-dropdown-picker';

import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import Util from '../../util/util';
import { useTranslation } from '../../i18n';

/**
 * @param {string} organization - Current organization
 * @param {boolean} readOnly - If true, show read-only view (for mobile)
 */
const RoutingMapper = ({ organization, readOnly = false }) => {
  const { t } = useTranslation(['admin', 'common']);

  const [routingRules, setRoutingRules] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [childOrgs, setChildOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add rule form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [destDropdownOpen, setDestDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const isWeb = checkIsWeb();

  const fetchRoutingConfig = useCallback(async () => {
    if (!organization) return;

    try {
      const response = await Util.get('Resty', '/items/organization/routing', {
        queryParams: { organization },
      });

      if (response?.data?.routing) {
        // Convert routing object to array of rules
        const routing = response.data.routing;
        const rules = Object.entries(routing).map(([tag, targetOrg]) => ({
          tag,
          targetOrg,
        }));
        setRoutingRules(rules);
      } else {
        setRoutingRules([]);
      }
    } catch (err) {
      console.error('Error fetching routing configuration:', err);
      throw err;
    }
  }, [organization]);

  const fetchTags = useCallback(async () => {
    if (!organization) return;

    try {
      const response = await Util.get('Resty', '/items/organization_configuration/all', {
        queryParams: { organization },
      });

      if (response?.items) {
        // Extract KPIs and Locations
        const tags = response.items
          .filter((item) => item.value_type === 'KPI' || item.value_type === 'Location')
          .map((item) => item.value);
        setAvailableTags([...new Set(tags)]); // Remove duplicates
      }
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  }, [organization]);

  const fetchChildOrgs = useCallback(async () => {
    if (!organization) return;

    try {
      const response = await Util.get('Resty', '/items/organization/children', {
        queryParams: { organization },
      });

      if (response?.items) {
        setChildOrgs(response.items.map((org) => org.organization));
      }
    } catch (err) {
      console.error('Error fetching child organizations:', err);
    }
  }, [organization]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([fetchRoutingConfig(), fetchTags(), fetchChildOrgs()]);
    } catch (err) {
      setError(t('routing.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [fetchRoutingConfig, fetchTags, fetchChildOrgs]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const handleAddRule = async () => {
    if (!selectedTag || !selectedDestination) return;

    setSaving(true);
    try {
      await Util.post('Resty', '/items/organization/routing', {
        body: {
          organization,
          tag: selectedTag,
          destination: selectedDestination,
        },
      });

      // Refresh routing rules in component and AsyncStorage cache
      await fetchRoutingConfig();
      await Util.setOrganizationSettings(organization);

      // Reset form
      setSelectedTag(null);
      setSelectedDestination(null);
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding routing rule:', err);
      setError(t('routing.addFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (tag) => {
    setDeleting(tag);
    try {
      await Util.del('Resty', '/items/organization/routing', {
        queryParams: { organization, tag },
      });

      // Refresh routing rules in component and AsyncStorage cache
      await fetchRoutingConfig();
      await Util.setOrganizationSettings(organization);
    } catch (err) {
      console.error('Error deleting routing rule:', err);
      setError(t('routing.deleteFailed'));
    } finally {
      setDeleting(null);
    }
  };

  // Get tags that aren't already in routing rules
  const getAvailableTagsForDropdown = () => {
    const usedTags = routingRules.map((rule) => rule.tag);
    return availableTags
      .filter((tag) => !usedTags.includes(tag))
      .map((tag) => ({ label: tag, value: tag }));
  };

  // Get child orgs as dropdown items
  const getChildOrgsForDropdown = () => {
    return childOrgs.map((org) => ({ label: org, value: org }));
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
          <CustomText style={styles.loadingText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('routing.loading')}
          </CustomText>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Feather name="alert-circle" size={20} color={AppStyles.colorSet.errorColor} />
          <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.errorColor}>
            {error}
          </CustomText>
          <Pressable onPress={loadAllData} style={styles.retryButton}>
            <CustomText size={13} color={AppStyles.colorSet.brandPrimary}>
              {t('routing.retry')}
            </CustomText>
          </Pressable>
        </View>
      </View>
    );
  }

  const canAddRules = !readOnly && isWeb && childOrgs.length > 0;
  const availableTagsForDropdown = getAvailableTagsForDropdown();
  const hasAvailableTags = availableTagsForDropdown.length > 0;

  return (
    <View style={styles.container}>
      <CustomText
        style={styles.heading}
        font="semiBold"
        size={18}
        color={AppStyles.colorSet.textPrimary}
      >
        {t('routing.title')}
      </CustomText>
      <CustomText
        style={styles.explanationText}
        font="regular"
        size={14}
        color={AppStyles.colorSet.textSecondary}
      >
        {t('routing.description')}
      </CustomText>

      {routingRules.length === 0 ? (
        <View style={styles.emptyState}>
          <Feather name="git-merge" size={32} color={AppStyles.colorSet.textMuted} />
          <CustomText style={styles.emptyText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('routing.empty')}
          </CustomText>
          <CustomText style={styles.emptySubtext} size={13} color={AppStyles.colorSet.textMuted}>
            {readOnly && !isWeb
              ? t('routing.webOnlyHint')
              : childOrgs.length === 0
                ? t('routing.needChildOrgs')
                : t('routing.addRulesHint')}
          </CustomText>
        </View>
      ) : (
        <View style={styles.rulesList}>
          {routingRules.map((rule, index) => (
            <View key={rule.tag || index} style={styles.ruleItem}>
              <View style={styles.ruleLeft}>
                <Feather name="tag" size={16} color={AppStyles.colorSet.brandPrimary} />
                <CustomText style={styles.ruleTag} size={14} color={AppStyles.colorSet.textPrimary}>
                  {rule.tag}
                </CustomText>
              </View>
              <Feather name="arrow-right" size={16} color={AppStyles.colorSet.textMuted} />
              <View style={styles.ruleRight}>
                <Feather name="folder" size={16} color={AppStyles.colorSet.textSecondary} />
                <CustomText style={styles.ruleOrg} size={14} color={AppStyles.colorSet.textPrimary}>
                  {rule.targetOrg}
                </CustomText>
              </View>
              {canAddRules && (
                <Pressable
                  onPress={() => handleDeleteRule(rule.tag)}
                  style={({ hovered, focused }) => [
                    styles.deleteButton,
                    { cursor: 'pointer' },
                    (hovered || focused) && styles.deleteButtonHovered,
                  ]}
                  disabled={deleting === rule.tag}
                  accessibilityLabel={t('routing.deleteRuleLabel', { tag: rule.tag })}
                  accessibilityRole="button"
                >
                  {deleting === rule.tag ? (
                    <ActivityIndicator size="small" color={AppStyles.colorSet.error} />
                  ) : (
                    <Feather name="trash-2" size={16} color={AppStyles.colorSet.error} />
                  )}
                </Pressable>
              )}
            </View>
          ))}
        </View>
      )}

      {readOnly && !isWeb && routingRules.length > 0 && (
        <View style={styles.webOnlyNotice}>
          <Feather name="monitor" size={16} color={AppStyles.colorSet.textMuted} />
          <CustomText style={styles.webOnlyText} size={13} color={AppStyles.colorSet.textMuted}>
            {t('routing.editOnWeb')}
          </CustomText>
        </View>
      )}

      {/* Add Rule Section - Web Only */}
      {canAddRules && (
        <View style={styles.addRuleSection}>
          {!showAddForm ? (
            <Pressable
              style={({ pressed, hovered, focused }) => [
                styles.addButton,
                { cursor: 'pointer' },
                hasAvailableTags && (hovered || focused) && styles.addButtonHovered,
                pressed && styles.addButtonPressed,
              ]}
              onPress={() => setShowAddForm(true)}
              disabled={!hasAvailableTags}
              accessibilityLabel={t('routing.addRuleLabel')}
              accessibilityRole="button"
            >
              <Feather
                name="plus"
                size={18}
                color={
                  hasAvailableTags
                    ? '#B45309' // Darker amber for accessibility (4.5:1 contrast)
                    : AppStyles.colorSet.textMuted
                }
              />
              <CustomText
                style={styles.addButtonText}
                size={14}
                font="medium"
                color={
                  hasAvailableTags
                    ? '#B45309' // Darker amber for accessibility (4.5:1 contrast)
                    : AppStyles.colorSet.textMuted
                }
              >
                {hasAvailableTags ? t('routing.addRule') : t('routing.allTagsRouted')}
              </CustomText>
            </Pressable>
          ) : (
            <View style={styles.addFormContainer}>
              <CustomText
                style={styles.addFormTitle}
                font="medium"
                size={15}
                color={AppStyles.colorSet.textPrimary}
              >
                {t('routing.addRule')}
              </CustomText>

              <View style={styles.dropdownRow}>
                <View style={[styles.dropdownWrapper, { zIndex: 3000 }]}>
                  <CustomText
                    style={styles.dropdownLabel}
                    size={13}
                    color={AppStyles.colorSet.textSecondary}
                  >
                    {t('routing.tagLabel')}
                  </CustomText>
                  <DropDownPicker
                    open={tagDropdownOpen}
                    value={selectedTag}
                    items={availableTagsForDropdown}
                    setOpen={setTagDropdownOpen}
                    setValue={setSelectedTag}
                    placeholder={t('routing.selectTag')}
                    style={styles.dropdown}
                    dropDownContainerStyle={styles.dropdownContainer}
                    textStyle={styles.dropdownText}
                    placeholderStyle={styles.dropdownPlaceholder}
                    zIndex={3000}
                    zIndexInverse={1000}
                  />
                </View>

                <View style={styles.arrowContainer}>
                  <Feather name="arrow-right" size={20} color={AppStyles.colorSet.textMuted} />
                </View>

                <View style={[styles.dropdownWrapper, { zIndex: 2000 }]}>
                  <CustomText
                    style={styles.dropdownLabel}
                    size={13}
                    color={AppStyles.colorSet.textSecondary}
                  >
                    {t('routing.destination')}
                  </CustomText>
                  <DropDownPicker
                    open={destDropdownOpen}
                    value={selectedDestination}
                    items={getChildOrgsForDropdown()}
                    setOpen={setDestDropdownOpen}
                    setValue={setSelectedDestination}
                    placeholder={t('routing.selectOrganization')}
                    style={styles.dropdown}
                    dropDownContainerStyle={styles.dropdownContainer}
                    textStyle={styles.dropdownText}
                    placeholderStyle={styles.dropdownPlaceholder}
                    zIndex={2000}
                    zIndexInverse={2000}
                  />
                </View>
              </View>

              <View style={styles.addFormButtons}>
                <Pressable
                  style={({ hovered, focused, pressed }) => [
                    styles.cancelButton,
                    { cursor: 'pointer' },
                    (hovered || focused) && styles.cancelButtonHovered,
                    pressed && { opacity: 0.7 },
                  ]}
                  onPress={() => {
                    setShowAddForm(false);
                    setSelectedTag(null);
                    setSelectedDestination(null);
                  }}
                  accessibilityLabel={t('common:actions.cancel')}
                  accessibilityRole="button"
                >
                  <CustomText size={14} color={AppStyles.colorSet.textSecondary}>
                    {t('common:actions.cancel')}
                  </CustomText>
                </Pressable>

                <Pressable
                  style={({ pressed, hovered, focused }) => [
                    styles.saveButton,
                    { cursor: 'pointer' },
                    selectedTag && selectedDestination && (hovered || focused) && styles.saveButtonHovered,
                    (!selectedTag || !selectedDestination) && styles.saveButtonDisabled,
                    pressed && styles.saveButtonPressed,
                  ]}
                  onPress={handleAddRule}
                  disabled={!selectedTag || !selectedDestination || saving}
                  accessibilityLabel={t('routing.saveRuleLabel')}
                  accessibilityRole="button"
                >
                  {saving ? (
                    <ActivityIndicator size="small" color={AppStyles.colorSet.textPrimary} />
                  ) : (
                    <>
                      <Feather name="check" size={16} color={AppStyles.colorSet.textPrimary} />
                      <CustomText
                        style={styles.saveButtonText}
                        size={14}
                        font="medium"
                        color={AppStyles.colorSet.textPrimary}
                      >
                        {t('routing.saveRule')}
                      </CustomText>
                    </>
                  )}
                </Pressable>
              </View>
            </View>
          )}
        </View>
      )}

      {/* No child orgs notice for web */}
      {!readOnly && isWeb && childOrgs.length === 0 && (
        <View style={styles.noChildOrgsNotice}>
          <Feather name="info" size={16} color={AppStyles.colorSet.textMuted} />
          <CustomText style={styles.noChildOrgsText} size={13} color={AppStyles.colorSet.textMuted}>
            {t('routing.createChildOrgs')}
          </CustomText>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 12,
    zIndex: 10,
    position: 'relative',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: hp(3),
  },
  loadingText: {
    marginLeft: 10,
  },
  errorContainer: {
    alignItems: 'center',
    paddingVertical: hp(2),
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    padding: 16,
  },
  errorText: {
    marginTop: 8,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    padding: 8,
  },
  heading: {
    marginTop: hp(1),
    marginBottom: hp(0.5),
  },
  explanationText: {
    marginBottom: hp(1.5),
    lineHeight: 20,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: hp(3),
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderStyle: 'dashed',
  },
  emptyText: {
    marginTop: 8,
  },
  emptySubtext: {
    marginTop: 4,
    textAlign: 'center',
  },
  rulesList: {
    marginBottom: hp(1),
  },
  ruleItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  ruleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  ruleTag: {
    marginLeft: 8,
  },
  ruleRight: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginLeft: 8,
  },
  ruleOrg: {
    marginLeft: 8,
  },
  deleteButton: {
    padding: 8,
    marginLeft: 8,
  },
  deleteButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
    borderRadius: 8,
  },
  webOnlyNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    marginTop: hp(1),
  },
  webOnlyText: {
    marginLeft: 8,
  },
  addRuleSection: {
    marginTop: hp(1),
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#D97706', // Darker amber for better visibility
    borderStyle: 'dashed',
    backgroundColor: '#FAFAFA',
  },
  addButtonHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: AppStyles.stateSet.hover,
  },
  addButtonPressed: {
    opacity: 0.7,
  },
  addButtonText: {
    marginLeft: 8,
  },
  addFormContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    position: 'relative',
    zIndex: 5000,
  },
  addFormTitle: {
    marginBottom: 16,
  },
  dropdownRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 16,
    zIndex: 3000, // Ensure dropdowns appear above buttons
    position: 'relative',
  },
  dropdownWrapper: {
    flex: 1,
  },
  dropdownLabel: {
    marginBottom: 6,
  },
  dropdown: {
    borderColor: '#E5E5E5',
    borderRadius: 8,
    minHeight: 44,
  },
  dropdownContainer: {
    borderColor: '#E5E5E5',
    borderRadius: 8,
    zIndex: 5000,
    elevation: 5000, // For Android
  },
  dropdownText: {
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
  },
  dropdownPlaceholder: {
    color: AppStyles.colorSet.textMuted,
  },
  arrowContainer: {
    paddingHorizontal: 12,
    paddingBottom: 12,
  },
  addFormButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    zIndex: 1, // Lower than dropdown to prevent overlap
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  cancelButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  saveButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  saveButtonDisabled: {
    backgroundColor: AppStyles.colorSet.textMuted,
    opacity: 0.5,
  },
  saveButtonPressed: {
    opacity: 0.8,
  },
  saveButtonText: {
    marginLeft: 6,
  },
  noChildOrgsNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    marginTop: hp(1),
  },
  noChildOrgsText: {
    marginLeft: 8,
  },
});

export default RoutingMapper;
